import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Avatar } from '@/components/avatar';
import { useToast } from '@/components/toast';
import {
  useMe,
  useUpdateProfile,
  useUploadAvatar,
  useDeleteAvatar,
} from '@/lib/auth';
import { useFormError } from '@/lib/form-error';

export function ProfileTab() {
  return (
    <div className="space-y-6">
      <AvatarCard />
      <ProfileCard />
    </div>
  );
}

function AvatarCard() {
  const me = useMe();
  const upload = useUploadAvatar();
  const remove = useDeleteAvatar();
  const { error, wrap } = useFormError();
  const inputRef = useRef<HTMLInputElement>(null);

  if (!me.data) return null;

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await wrap(() => upload.mutateAsync(file));
  }

  const hasAvatar = me.data.avatar_url != null;

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Avatar</h3>
        <p className="text-xs text-[var(--color-foreground-muted)]">
          Square images work best. JPG, PNG, WebP, or GIF up to 4 MB.
        </p>
      </CardHeader>
      <CardBody>
        <div className="flex items-center gap-5">
          <Avatar src={me.data.avatar_thumb_url} name={me.data.name} size="lg" />
          <div className="flex flex-col gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              hidden
              onChange={onPick}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => inputRef.current?.click()}
                disabled={upload.isPending}
              >
                <Upload className="size-4" />
                {hasAvatar ? 'Replace' : 'Upload'}
              </Button>
              {hasAvatar ? (
                <Button
                  variant="ghost"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                >
                  <Trash2 className="size-4" /> Remove
                </Button>
              ) : null}
            </div>
            {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function ProfileCard() {
  const me = useMe();
  const update = useUpdateProfile();
  const { error, wrap } = useFormError();
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  if (!me.data) {
    return <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>;
  }

  const initial = me.data;
  const currentName = name || initial.name;
  const currentEmail = email || initial.email;
  const dirty = currentName !== initial.name || currentEmail !== initial.email;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() => update.mutateAsync({ name: currentName, email: currentEmail }));
    if (ok) toast.success('Profile updated');
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Profile</h3>
        <p className="text-xs text-[var(--color-foreground-muted)]">
          Your name and login email.
        </p>
      </CardHeader>
      <CardBody>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Field label="Name">
            <Input
              value={currentName}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={currentEmail}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={!dirty || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}
